
    import { captureException } from '@sentry/vue'
    import { computed, defineComponent, onBeforeMount, ref, watch } from 'vue'

    import type { IChatMessage } from '@/common/models/data/dashboard/chat/chat-message'
    import Echo from '@/app/echo'

    import ChatComposer from '@/app/components/sidebar/chat/ChatComposer.vue'
    import ChatLog from '@/app/components/sidebar/chat/ChatLog.vue'
    import ChatRetry from '@/app/components/sidebar/chat/ChatRetry.vue'

    import { SpamError } from '@/app/errors/spam-error'

    import { useChatStore } from '@/app/stores/chat'
    import { useGdprStore } from '@/app/stores/gdpr'

    import { useGtag } from '@/common/composables/libs/gtag'
    import { useI18n } from 'vue-i18n-composable'
    import { useSweetAlert } from '@/common/composables/sweet-alert'
    import { useSharedSettingsStore } from '@/common/stores/shared-settings'

    export default defineComponent({
        components: {
            ChatComposer,
            ChatLog,
            ChatRetry
        },
        props: {
            shown: {
                type: Boolean,
                required: true
            }
        },
        setup (props) {
            const gtag = useGtag()
            const i18n = useI18n()

            const chatStore = useChatStore()
            const gdprStore = useGdprStore()
            const settingsStore = useSharedSettingsStore()
            const { showToast } = useSweetAlert()

            const log = ref<InstanceType<typeof ChatLog>>()

            const connecting = computed(() => chatStore.connecting)
            const disabled = computed(() => settingsStore.setting('disable-chat') === '1')
            const loading = computed(() => chatStore.loading)
            const messages = computed(() => chatStore.messages)
            const offline = computed(() => chatStore.offline)

            const gdprAccepted = computed(() => gdprStore.accepted)

            const initEcho = (): void => {
                Echo.channel('radio17-chat')
                    .listen('.message.posted', (e: { message: IChatMessage }) => {
                        chatStore.prependMessage(e.message)
                        log.value?.scrollDown()
                    })
            }

            const leaveEcho = (): void => {
                Echo.leave('radio17-chat')
            }

            const init = async (): Promise<void> => {
                gdprStore.accept()
                gtag.optIn()

                await chatStore.load()

                if (props.shown) {
                    initEcho()
                }
            }

            const loadPrevious = async (): Promise<void> => {
                try {
                    await chatStore.loadPrevious()
                } catch (e) {
                    captureException(e, { level: 'error' })

                    await showToast(
                        i18n.t('chat.errors.load-previous').toString(),
                        'error'
                    )
                }
            }

            const sendMessage = async (): Promise<void> => {
                if (chatStore.message === '') {
                    return
                }

                try {
                    await chatStore.sendMessage()

                    gtag.event(i18n.t('analytics.chat.send').toString(), {
                        event_category: 'Chat'
                    })
                } catch (e) {
                    captureException(e, { level: 'error' })

                    if (e instanceof SpamError) {
                        await showToast(
                            i18n.t('chat.errors.throttle').toString(),
                            'warning'
                        )

                        gtag.event(i18n.t('analytics.chat.spam').toString(), {
                            event_category: 'Chat'
                        })
                    } else {
                        await showToast(
                            i18n.t('chat.errors.send-message').toString(),
                            'error'
                        )
                    }
                }
            }

            watch(gdprAccepted, async value => {
                if (value) {
                    await init()
                }
            })

            watch(() => props.shown, async value => {
                if (value) {
                    await chatStore.load()

                    initEcho()

                    gtag.event(i18n.t('analytics.chat.open').toString(), {
                        event_category: 'Chat'
                    })
                } else {
                    leaveEcho()

                    gtag.event(i18n.t('analytics.chat.close').toString(), {
                        event_category: 'Chat'
                    })
                }
            })

            onBeforeMount(async (): Promise<void> => {
                if (props.shown) {
                    await chatStore.load()
                }
            })

            return {
                connecting,
                disabled,
                gdprAccepted,
                loading,
                log,
                messages,
                offline,
                init,
                load: chatStore.load,
                loadPrevious,
                sendMessage
            }
        }
    })
